/**
 * Демонстрационные страницы для отработки attachment lifecycle.
 * При форке скелета удалить или модифицировать под конкретное приложение.
 * Стилистика таблицы по образцу web-client (display-stocks-data-table, products-table-columns).
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import {
  Button,
  DataTable,
  Paper,
  ConfirmModal,
  ImageWithFallback,
  MenuButton,
  PaginationControlled,
  CollapsibleListFilters,
  FilterFieldLabel,
  FilterNativeSelect,
  FilterTextInput,
  FiltersPanel,
} from '../uikit';
import type { IDataTableColumn, ISortedTable, OrderDirectionType } from '../uikit';
import type { Product } from '../api/products';
import {
  findProducts,
  deleteProducts,
  getCategories,
  type FindProductsParams,
} from '../api/products';
import { toUserMessage } from '../api/client';
import { fileUriToSrc } from '../api/files';
import { IconEdit, IconDelete } from '../uikit/components/icons';
import { formatMoney } from '../utils/formatNumber';

const PRODUCT_DELETE_BLOCKED =
  'Изделие используется в заказах или сменах. Удаление недоступно.';

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin: 0 -${({ theme }) => theme.space[2]}px 12px;
  padding-left: ${({ theme }) => theme.space[2]}px;
  padding-right: ${({ theme }) => theme.space[2]}px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  line-height: 1.5;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ErrorRow = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  ${({ theme }) => theme.device.mobile} {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const PageLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const TableSection = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const EmptyImagePlaceholder = styled.div`
  width: ${({ theme }) => theme.space[5]}px;
  height: ${({ theme }) => theme.space[5]}px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius}px;
  background: ${({ theme }) => theme.colors.highlight.blue25};

  img {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

function getProductColumns(
  onEdit: (item: Product) => void,
  onDelete: (item: Product) => void,
): IDataTableColumn<Product>[] {
  return [
    {
      key: 'image',
      header: 'Изображение',
      sortable: false,
      fixedWidth: 70,
      truncate: false,
      cellStyle: { pl: 2 },
      render: (value, item) => {
        const img = value as Product['image'];
        if (!img?.uri) {
          return (
            <Link to={`/products/${item.id}`} style={{ display: 'block' }}>
              <EmptyImagePlaceholder aria-label="Нет изображения">
                <img src="/avatar-blank.svg" alt="" aria-hidden />
              </EmptyImagePlaceholder>
            </Link>
          );
        }
        const src = fileUriToSrc(img.uri);
        const content = (
          <ImageWithFallback
            src={src}
            alt={item.image?.filename ?? ''}
            objectFit="cover"
            fallbackLabel="Не найден"
            style={{ width: 46, height: 46, borderRadius: 4 }}
          />
        );
        return (
          <Link to={`/products/${item.id}`} style={{ display: 'block' }}>
            {content}
          </Link>
        );
      },
    },
    {
      key: 'name',
      header: 'Название',
      sortable: true,
      fixedWidth: 240,
      cellStyle: { px: 2 },
      render: (value, item) => {
        const name = (value as string) || '—';
        return (
          <Link
            to={`/products/${item.id}`}
            style={{
              color: 'inherit',
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            {name}
          </Link>
        );
      },
    },
    {
      key: 'description',
      header: 'Описание',
      sortable: false,
      fixedWidth: 280,
      cellStyle: { px: 2 },
      render: (value) => (value ? String(value) : '—'),
    },
    {
      key: 'category',
      header: 'Категория',
      sortable: true,
      fixedWidth: 140,
      cellStyle: { px: 2 },
      render: (value) => (value ? String(value) : '—'),
    },
    {
      key: 'costPrice',
      header: 'Себестоимость, руб',
      sortable: true,
      fixedWidth: 140,
      cellStyle: { px: 2 },
      render: (value) => formatMoney(value as string | null),
    },
    {
      key: 'salePrice',
      header: 'Цена продажи, руб',
      sortable: true,
      fixedWidth: 140,
      cellStyle: { px: 2 },
      render: (value) => formatMoney(value as string | null),
    },
    {
      key: 'actions',
      header: 'Действия',
      sortable: false,
      fixedWidth: 84,
      truncate: false,
      cellStyle: { px: 1, textAlign: 'right' },
      render: (_, item) => {
        const blocked = Boolean(item.referencedInOrdersOrShifts);
        return (
          <div style={{ display: 'flex', gap: 0, justifyContent: 'flex-end' }}>
            <MenuButton
              size={28}
              aria-label="Редактировать"
              onClick={() => onEdit(item)}
            >
              <IconEdit />
            </MenuButton>
            {blocked ? (
              <span title={PRODUCT_DELETE_BLOCKED} style={{ display: 'inline-flex' }}>
                <MenuButton
                  size={28}
                  disabled
                  aria-label={PRODUCT_DELETE_BLOCKED}
                >
                  <IconDelete />
                </MenuButton>
              </span>
            ) : (
              <MenuButton
                size={28}
                aria-label="Удалить"
                onClick={() => onDelete(item)}
              >
                <IconDelete />
              </MenuButton>
            )}
          </div>
        );
      },
    },
  ];
}

export function ProductsListPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [items, setItems] = useState<Product[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [sort, setSort] = useState<ISortedTable>({ orderBy: 'name', orderDirection: 'ASC' });
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [nameSearch, setNameSearch] = useState('');
  const [categories, setCategories] = useState<string[]>([]);

  const loadCategories = useCallback(async () => {
    try {
      const items = await getCategories();
      setCategories(items);
    } catch {
      setCategories([]);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params: FindProductsParams = {
        page,
        itemsPerPage,
        search: nameSearch.trim() || undefined,
        category: categoryFilter || undefined,
        order: [{ field: sort.orderBy ?? 'name', direction: sort.orderDirection ?? 'ASC' }],
      };
      const result = await findProducts(params);
      setItems(result.items);
      setCount(result.count);
    } catch (err) {
      setItems([]);
      setCount(0);
      setLoadError(toUserMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page, itemsPerPage, categoryFilter, nameSearch, sort.orderBy, sort.orderDirection]);

  useEffect(() => {
    void loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories]);

  const handleSort = (key: string, direction: OrderDirectionType) => {
    setSort({ orderBy: key, orderDirection: direction });
    setPage(1);
  };

  const handleCategoryFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCategoryFilter(e.target.value);
    setPage(1);
  };

  const handleEditClick = (product: Product) => {
    navigate(`/products/${product.id}/edit`);
  };

  const handleDeleteClick = (product: Product) => {
    if (product.referencedInOrdersOrShifts) {
      return;
    }
    setDeleteTarget(product);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    setLoadError(null);
    try {
      await deleteProducts([deleteTarget.id]);
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      await loadProducts();
    } catch (err) {
      setLoadError(toUserMessage(err));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <Paper padding={16} shadow={0} style={{ marginBottom: 0, height: '100%' }}>
        <PageLayout>
          <HeaderRow>
            <PageTitle>Изделия</PageTitle>
            <Button
              variant="filled"
              color="primary"
              size="s"
              onClick={() => navigate('/products/new')}
            >
              Создать
            </Button>
          </HeaderRow>
          <CollapsibleListFilters
            activeFilterCount={(categoryFilter ? 1 : 0) + (nameSearch.trim() ? 1 : 0)}
          >
            <FiltersPanel $borderless>
              <FilterFieldLabel htmlFor="product-name-filter">
                Название:
                <FilterTextInput
                  id="product-name-filter"
                  type="search"
                  value={nameSearch}
                  onChange={(e) => {
                    setNameSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Поиск по названию"
                  aria-label="Фильтр по названию изделия"
                  $maxWidthPx={280}
                />
              </FilterFieldLabel>
              <FilterFieldLabel htmlFor="product-category-filter">
                Категория:
                <FilterNativeSelect
                  id="product-category-filter"
                  value={categoryFilter}
                  onChange={handleCategoryFilterChange}
                  aria-label="Фильтр по категории"
                  $minWidthPx={160}
                  $maxWidthPx={280}
                >
                  <option value="">Все</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </FilterNativeSelect>
              </FilterFieldLabel>
            </FiltersPanel>
          </CollapsibleListFilters>
          <TableSection>
            <DataTable
              data={items}
              columns={getProductColumns(handleEditClick, handleDeleteClick)}
              orderBy={sort.orderBy}
              orderDirection={sort.orderDirection}
              onSort={handleSort}
              loading={loading}
              settings={{ horizontalLine: true, backlightLine: true }}
              minHeight={0}
              height="100%"
              placeholder={{ text: 'Изделия не найдены' }}
            />
          </TableSection>
          {loadError && (
            <ErrorRow role="alert">
              <span style={{ color: theme.colors.error.main, fontSize: theme.fontSizes[5] }}>{loadError}</span>
              <Button variant="outlined" color="primary" size="s" onClick={() => void loadProducts()}>
                Повторить
              </Button>
            </ErrorRow>
          )}
          <PaginationControlled
            page={page}
            itemsPerPage={itemsPerPage}
            totalItems={count}
            setPage={setPage}
            setItemsPerPage={setItemsPerPage}
          />
        </PageLayout>
      </Paper>
      <ConfirmModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Удалить изделие?"
        message={
          deleteTarget ? (
            <>Вы уверены, что хотите удалить изделие «{deleteTarget.name}»?</>
          ) : undefined
        }
        confirmText="Удалить"
        cancelText="Отмена"
        confirmColor="error"
        loading={deleteLoading}
      />
    </>
  );
}
